import type { Dispatch, ElementType, SetStateAction } from "react";

import { BanIcon, CircleCheckIcon, LoaderCircleIcon } from "@lucide/icons";
import { UploadIcon, type LucideIconData } from "@lucide/icons";

import { handleClickForKeyboard } from "simplydo/core/accessibility";
import { pluralise } from "simplydo/core/helpers";
import { styled } from "simplydo/react/styled";
import { H3, H5 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Progress } from "simplydo/react/ui/progress";
import { Segment } from "simplydo/react/ui/segment";

import { type DragFile, type UploadState, verifyType } from "./uploadUtils";

type StyledDropZoneProps = {
  children: React.ReactNode;
  style?: React.CSSProperties;
};

export const StyledDropZone = (props: StyledDropZoneProps) => {
  return <Segment placeholder {...props} />;
};

const DropZoneStyling = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 20px;

  > div {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  > h5,
  > div > h5,
  > div > h3 {
    margin: 0 0 3px 0;
    display: block;
    color: #808080;
    > u {
      color: ${(props) =>
        props.theme
          ? props.theme.primaryColour
          : "var(--colorBrandForegroundLink, #0078d4)"};
      cursor: pointer;
    }
  }
  > i.icon,
  > svg.icon {
    font-size: 3em !important;
    color: #808080;
  }
`;

export type DropZoneConfigProps = {
  DropZoneComponent?: ElementType;
  title?: string;
  subtitle?: string;
  defaultIcon?: LucideIconData;
  onClick?: () => void;
};

export type DropZoneProps = {
  uploadState: UploadState;
  setUploadState: Dispatch<SetStateAction<UploadState>>;
  accept?: string;
  disabled?: boolean;
  draggedOverFiles?: Array<DragFile>;
  allowMultiple?: boolean;
  style?: React.CSSProperties;
} & DropZoneConfigProps;

export const DropZone = ({
  disabled,
  accept,
  uploadState,
  draggedOverFiles,
  allowMultiple,
  style,
  onClick,
  title,
  subtitle,
  defaultIcon = UploadIcon,
  DropZoneComponent = StyledDropZone,
}: DropZoneProps) => {
  if (uploadState.isUploading) {
    return (
      <DropZoneComponent style={style}>
        <DropZoneStyling>
          <Icon size="large" icon={LoaderCircleIcon} />
          <Progress
            percent={uploadState.uploadProgress}
            indicating
            progress
            style={{ width: "100%" }}
          >
            Uploading...
          </Progress>
        </DropZoneStyling>
      </DropZoneComponent>
    );
  }

  if (disabled) {
    return (
      <DropZoneComponent style={style}>
        <DropZoneStyling>
          <Icon size="large" icon={BanIcon} />
          <H5>Cannot upload more files</H5>
        </DropZoneStyling>
      </DropZoneComponent>
    );
  }

  if ((draggedOverFiles?.length ?? 0) > 0) {
    if (!allowMultiple && (draggedOverFiles?.length ?? 0) > 1) {
      return (
        <DropZoneComponent style={style}>
          <DropZoneStyling>
            <Icon size="large" icon={BanIcon} />
            <H5>Cannot upload more than one file</H5>
          </DropZoneStyling>
        </DropZoneComponent>
      );
    }
    if (accept && draggedOverFiles) {
      for (let i = 0; i < draggedOverFiles.length; i++) {
        const draggedFile = draggedOverFiles[i];
        if (!draggedFile) continue;
        const typeCheck = verifyType(accept, draggedFile.type);
        if (!typeCheck.allowed) {
          return (
            <DropZoneComponent style={style}>
              <DropZoneStyling>
                <Icon size="large" icon={BanIcon} />
                <H5>{typeCheck.error}</H5>
              </DropZoneStyling>
            </DropZoneComponent>
          );
        }
      }
    }
    return (
      <DropZoneComponent style={style}>
        <DropZoneStyling>
          <Icon size="large" icon={CircleCheckIcon} />
          <H5>
            Drop the {pluralise(draggedOverFiles?.length ?? 0, "file", "files")}{" "}
            to start the upload
          </H5>
        </DropZoneStyling>
      </DropZoneComponent>
    );
  }

  return (
    <DropZoneComponent style={style} onClick={onClick}>
      <DropZoneStyling>
        <Icon size="large" icon={defaultIcon} />
        <div>
          {title ? <H3>{title}</H3> : null}
          {subtitle ? <H5>{subtitle}</H5> : null}
          <H5 style={{ marginTop: 6 }}>
            Drop a file here to upload, or{" "}
            <u
              role="button"
              tabIndex={0}
              onKeyDown={handleClickForKeyboard(onClick)}
            >
              click to browse
            </u>
            .{allowMultiple ? " You can upload multiple files at once." : ""}
          </H5>
        </div>
      </DropZoneStyling>
    </DropZoneComponent>
  );
};
